import { DropboxFilesListTable } from '@components/display/tables';
import {
    useFirmwareFavoriteMutation,
    useFirmwareUnfavoriteMutation,
    useLazyGetFirmwareDropboxFilesLoadedPathsQuery,
} from '@firmwares/services';
import { useAuth } from '@hooks/useAuth';
import { useCallback } from 'react';
import { defineMessage } from 'react-intl';

export const FirmwaresListView = () => {
    const { isLoggedIn } = useAuth();
    const [triggerLoadedPaths] = useLazyGetFirmwareDropboxFilesLoadedPathsQuery();
    const [favoriteFile] = useFirmwareFavoriteMutation();
    const [unfavoriteFile] = useFirmwareUnfavoriteMutation();

    const loadPage = useCallback(
        async ({
            queryPath,
            sortParam,
            pageSize,
            loadedPaths,
        }: {
            queryPath: string;
            sortParam: string | null;
            pageSize: number;
            loadedPaths: string[];
        }) => {
            const pageData = await triggerLoadedPaths(
                {
                    path: queryPath,
                    page_size: pageSize,
                    ordering: sortParam ?? undefined,
                    loaded_paths: loadedPaths,
                },
                true
            ).unwrap();

            return {
                results: pageData.results ?? [],
                count: pageData.count,
            };
        },
        [triggerLoadedPaths]
    );

    const handleFavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await favoriteFile({ path, folderPath }).unwrap();
        },
        [favoriteFile]
    );

    const handleUnfavorite = useCallback(
        async ({ path, folderPath }: { path: string; folderPath: string }) => {
            await unfavoriteFile({ path, folderPath }).unwrap();
        },
        [unfavoriteFile]
    );

    return (
        <DropboxFilesListTable
            routeSegment="firmware"
            downloadType="firmwares"
            emptyListText={defineMessage({
                id: 'firmwares.emptyList',
                defaultMessage: 'No firmwares found in this folder.',
            })}
            loadErrorMessage={defineMessage({
                id: 'firmwares.loadError',
                defaultMessage: 'Error loading firmwares',
            })}
            loadPage={loadPage}
            onFavorite={isLoggedIn ? handleFavorite : undefined}
            onUnfavorite={isLoggedIn ? handleUnfavorite : undefined}
        />
    );
};
